import type {ReactElement} from 'react';
import React, {useRef, useState, useEffect} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {getNoOfGridItems} from '@Components/base-grid';
import {setHasLanguageSelectorJustClosed} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import type {AddAiItemButtonProps} from './add-ai-item-button.types';
import {AddAiItemPopOverContentState, AI_IMAGE_PANEL_ID, AI_SUBTITLE_PANEL_ID, AI_VOICE_PANEL_ID} from './add-ai-item-button.types';
import styles from './add-ai-item-button.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AiSubtitleMenuOpenedFrom, getAiSubtitlesPanelHeightClass} from '@Libraries/ai-transcript.library';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {AddAiItemDropdownPopupContent} from '@Components/add-ai-item-button/components/add-ai-item-dropdown-popup-content/add-ai-item-dropdown-popup-content';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import {getGridIdForPanel} from '@Libraries/text-to-image-library';
import {AiVoicePanelDataKey, AiVoicePanelGenerationState, AiVoicePanelHeightChangingEvents} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {showLoading} from '@Libraries/loading-toast-library';
import {getLoadingKeyForAiVoice} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {AddAiItemPopUpSubPanel} from '@Components/add-ai-item-button/components/add-ai-item-dropdown-popup-content/add-ai-item-dropdown-popup-content.types';
import {getHasUserSeenAiVoice, markUserHasSeenAiVoice} from '@Libraries/text-to-speech.library';
import {ADD_ITEM_BAR_POPUP_OFFSET, ADD_ITEM_BAR_TOP_OFFSET} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';

export function AddAiItemButton({arrowClasses = '', popupClassName = '', ...props}: AddAiItemButtonProps): ReactElement {
  const dispatch = useAppDispatch();
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const [showAiVoiceAsNewFeature, setShowAiVoiceAsNewFeature] = useState<boolean>(false);
  const [isOpened, setIsOpened] = useState(false);
  const [dropdownContentState, setDropdownContentState] = useState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
  const numberOfGridItemsInAiImageGrid = useAppSelector((state) => {
    return getNoOfGridItems(state, getGridIdForPanel(AI_IMAGE_PANEL_ID));
  });
  const aiSubtitleReduxState = useAppSelector((state) => {
    return state.aiSubtitle;
  });
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });
  const hasUserMediaPanelJustClosed = useAppSelector((state) => {
    return state.panels.panelHashmap['user-media-modal-panel'];
  });
  const hasLanguageSelectorJustClosed = useAppSelector((state) => {
    return state.aiSubtitle.aiSubtitlesPanel.hasLanguageSelectorJustClosed;
  });
  const aiVoicePanelHeightState = useAppSelector((state): undefined | AiVoicePanelHeightChangingEvents => {
    return state.panels.panelHashmap[AI_VOICE_PANEL_ID]?.data?.[AiVoicePanelDataKey.STATE] as AiVoicePanelHeightChangingEvents;
  });
  const aiVoicePanelGenerationState = useAppSelector((state): AiVoicePanelGenerationState | undefined => {
    return state.panels.panelHashmap[AI_VOICE_PANEL_ID]?.data?.[AiVoicePanelDataKey.GENERATION_STATE] as AiVoicePanelGenerationState;
  });

  const getSelector = (): ReactElement => {
    return (
      <AddItemButton
        boldText
        isDisabled={false}
        isSelected={isOpened}
        id="toolbar-ai-images"
        text={window.i18next.t('pmwjs_ai')}
        icon="icon-ai"
        className={isOpened ? styles.selectedButton : undefined}
        textClasses={styles.selectorText}
        iconClasses={styles.selectorIcon}
      />
    );
  };

  const getNewFeatureMarkers = (): AddAiItemPopUpSubPanel[] => {
    const newFeatureMarkers: AddAiItemPopUpSubPanel[] = [];
    if (showAiVoiceAsNewFeature) {
      newFeatureMarkers.push(AddAiItemPopUpSubPanel.AI_VOICE);
    }
    return newFeatureMarkers;
  };

  const onSubPanelBack = (): void => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_IMAGE:
      case AddAiItemPopOverContentState.AI_SUBTITLES:
      case AddAiItemPopOverContentState.AI_VOICE:
        setDropdownContentState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
        break;
      default:
        break;
    }
  };

  const onDone = (): void => {
    setIsOpened(false);
    setDropdownContentState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
  };

  const onClickAiSubtitlesOption = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_MENU);
    setDropdownContentState(AddAiItemPopOverContentState.AI_SUBTITLES);
  };

  const onClickAiImagesOption = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_OPEN, {[GA4EventParamName.MENU_OPENED_FROM]: AiSubtitleMenuOpenedFrom.ADD_AI_OPTION});
    setDropdownContentState(AddAiItemPopOverContentState.AI_IMAGE);
  };

  const onClickAiVoiceOption = (): void => {
    void markUserHasSeenAiVoice();
    setShowAiVoiceAsNewFeature(false);
    setDropdownContentState(AddAiItemPopOverContentState.AI_VOICE);
  };

  const getPopupContent = (): ReactElement => {
    return (
      <AddAiItemDropdownPopupContent
        showAsNewFeature={getNewFeatureMarkers()}
        dropdownContentState={dropdownContentState}
        onSubPanelBack={onSubPanelBack}
        onDone={onDone}
        onClickAiSubtitlesOption={onClickAiSubtitlesOption}
        onClickAiImagesOption={onClickAiImagesOption}
        onClickAiVoiceOption={onClickAiVoiceOption}
        AiImagePanelId={AI_IMAGE_PANEL_ID}
        AiVoicePanelId={AI_VOICE_PANEL_ID}
        AiSubtitlePanelId={AI_SUBTITLE_PANEL_ID}
      />
    );
  };

  const getHeightClassForAiVoice = (): string => {
    switch (aiVoicePanelHeightState) {
      case AiVoicePanelHeightChangingEvents.GENERATING:
      case AiVoicePanelHeightChangingEvents.GENERATED_RESULT:
        return styles.aiVoiceContainerLoadingStyles;
      case AiVoicePanelHeightChangingEvents.DEFAULT:
      default:
        return '';
    }
  };

  const getClassesForPopup = (): string => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN:
        return styles.aiPopup;
      case AddAiItemPopOverContentState.AI_IMAGE:
        return `${styles.aiImageContainerStyles} ${numberOfGridItemsInAiImageGrid > 0 ? styles.fullAiImageGridContent : styles.emptyAiImageGridContent}`;
      case AddAiItemPopOverContentState.AI_SUBTITLES:
        return `${styles.aiSubtitlesPanelPopupContainerStyles} ${getAiSubtitlesPanelHeightClass(aiSubtitleState, Object.values(aiSubtitleReduxState.aiSubtitlesPanel.items).length, aiSubtitleReduxState.generationsBatch.totalItemsToGenerateSubtitlesFor, !isPremium.current)}`;
      case AddAiItemPopOverContentState.AI_VOICE:
        return `${styles.aiVoiceContainerStyles} ${getHeightClassForAiVoice()}`;
      default:
        return '';
    }
  };

  const shouldCloseOnOuterClick = (): boolean => {
    if (hasLanguageSelectorJustClosed) {
      setTimeout(() => {
        dispatch(setHasLanguageSelectorJustClosed(false));
      }, 10);

      return false;
    }

    return hasUserMediaPanelJustClosed === undefined;
  };

  const onAiVoicePopoverClose = (): void => {
    if (aiVoicePanelGenerationState === AiVoicePanelGenerationState.GENERATING) {
      showLoading(getLoadingKeyForAiVoice(AI_VOICE_PANEL_ID), {text: window.i18next.t('pmwjs_generating_audio')});
    }
  };

  const triggerCloseForContentState = (): void => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_VOICE:
        onAiVoicePopoverClose();
        break;
      default:
        break;
    }
  };

  const onClose = (): void => {
    if (isOpened) {
      triggerCloseForContentState();
      setIsOpened(false);
    }
  };

  const initAiVoiceNewStatus = async (): Promise<void> => {
    try {
      const response = await getHasUserSeenAiVoice();
      setShowAiVoiceAsNewFeature(!response);
    } catch (e) {
      setShowAiVoiceAsNewFeature(false);
    }
  };

  const init = (): void => {
    void initAiVoiceNewStatus();
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <Dropdown
      doesCloseOnOuterClick={shouldCloseOnOuterClick()}
      open={isOpened}
      doCloseOnInnerClick={false}
      className="js-toolbar-text"
      id="add-ai-item-pop-over"
      key="toolbar-add-ai-item-pop-over"
      data-testid={props['data-testid']}
      alignment={ALIGNMENT_TYPE.RIGHT}
      selector={getSelector()}
      popup={getPopupContent()}
      popupOffset={ADD_ITEM_BAR_POPUP_OFFSET}
      topOffset={ADD_ITEM_BAR_TOP_OFFSET}
      popUpHasCustomHeight
      maxHeight={650}
      popUpHasCustomWidth
      useDefaultArrow
      popupClassName={`${popupClassName} ${styles.popoverBaseStyle} ${getClassesForPopup()}`}
      popupSpacingClass={dropdownContentState !== AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN ? 'spacing-p-0' : ''}
      arrowClasses={arrowClasses}
      onOpen={(): void => {
        if (!isOpened) {
          setIsOpened(true);
        }
      }}
      onClose={onClose}
      isResumable
    />
  );
}
